import { z } from "zod";

// The device credential and the last profile, in IndexedDB (ADR-0019: the PWA's
// origin-scoped storage). Raw IndexedDB API: one database, one key-value store.
// If IndexedDB is unavailable (some private modes), values live in memory and the
// device must be set up again after a reload.

const DB_NAME = "tutor-device";
const STORE = "device";
const DEVICE_KEY = "device";
const LAST_PROFILE_KEY = "lastProfile";

const deviceRecord = z.object({ deviceId: z.string(), credential: z.string().min(1) });
export type DeviceRecord = z.infer<typeof deviceRecord>;

export interface StoredDevice {
  readonly device: DeviceRecord | undefined;
  readonly lastProfileId: string | undefined;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("IndexedDB request failed"));
    };
  });
}

let opening: Promise<IDBDatabase> | undefined;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgrading or deleting the database: let it, and reopen next time.
      db.onversionchange = () => {
        db.close();
        opening = undefined;
      };
      resolve(db);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("IndexedDB open failed"));
    };
    req.onblocked = () => {
      reject(new Error("IndexedDB open blocked"));
    };
  }).catch((error: unknown) => {
    opening = undefined;
    throw error;
  });
  return opening;
}

const memory = new Map<string, unknown>();

async function get(key: string): Promise<unknown> {
  try {
    const db = await open();
    return await done(db.transaction(STORE, "readonly").objectStore(STORE).get(key));
  } catch {
    return memory.get(key);
  }
}

async function put(key: string, value: unknown): Promise<void> {
  memory.set(key, value);
  try {
    const db = await open();
    await done(db.transaction(STORE, "readwrite").objectStore(STORE).put(value, key));
  } catch {
    // Kept in memory only.
  }
}

async function remove(key: string): Promise<void> {
  memory.delete(key);
  try {
    const db = await open();
    await done(db.transaction(STORE, "readwrite").objectStore(STORE).delete(key));
  } catch {
    // Nothing persisted.
  }
}

export async function loadDevice(): Promise<StoredDevice> {
  const [device, lastProfileId] = await Promise.all([get(DEVICE_KEY), get(LAST_PROFILE_KEY)]);
  const parsedDevice = deviceRecord.safeParse(device);
  const parsedProfile = z.string().safeParse(lastProfileId);
  return {
    device: parsedDevice.success ? parsedDevice.data : undefined,
    lastProfileId: parsedProfile.success ? parsedProfile.data : undefined,
  };
}

export function saveDevice(device: DeviceRecord): Promise<void> {
  return put(DEVICE_KEY, { deviceId: device.deviceId, credential: device.credential });
}

export function saveLastProfile(profileId: string): Promise<void> {
  return put(LAST_PROFILE_KEY, profileId);
}

/** Forgets the device, e.g. after a parent revoked it. */
export async function clearDevice(): Promise<void> {
  await Promise.all([remove(DEVICE_KEY), remove(LAST_PROFILE_KEY)]);
}

/** Test helper: drops the cached connection so a fresh database can be opened. */
export function resetStorageForTests(): void {
  memory.clear();
  opening = undefined;
}
